import React from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import { Checkbox } from '@vehix/portal/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@vehix/portal/components/ui/collapsible';
import { ChevronDown, Settings } from 'lucide-react';
import { EmailChipsInput } from './EmailChipsInput';

/** Run-mode options, worded identically to the Run Pipeline dialog. */
const RUN_MODES: ReadonlyArray<{
  value: 'auto' | 'full' | 'delta';
  label: string;
  desc: string;
}> = [
  { value: 'auto', label: 'Auto', desc: 'Full run if first time, delta otherwise' },
  { value: 'full', label: 'Full Run', desc: 'Re-crawl all inventory' },
  { value: 'delta', label: 'Delta Only', desc: 'Process existing inventory' }
];

const VIN_PROCESSORS: ReadonlyArray<{ value: 'api' | 'selenium'; label: string; desc: string }> = [
  { value: 'api', label: 'API-based', desc: 'Fast, uses HTTP APIs' },
  { value: 'selenium', label: 'Selenium-based', desc: 'Browser automation, slower' }
];

/**
 * A titled block of related settings.
 *
 * The section was a flat two-column grid of unrelated controls, so a checkbox
 * about VIN processing could sit beside a text note and read as equally
 * important. Headings make the four concerns legible at a glance.
 */
const Group: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className='space-y-2 rounded-md border bg-muted/20 px-3 py-2.5'>
    <h4 className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>
      {title}
    </h4>
    {children}
  </section>
);

interface CheckboxFieldProps {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  hint: string;
}

/** Checkbox with its explanation indented beneath, so the two never drift apart. */
const CheckboxField: React.FC<CheckboxFieldProps> = ({
  id,
  checked,
  disabled = false,
  onChange,
  label,
  hint
}) => (
  <div className='space-y-1'>
    <div className='flex items-center gap-2'>
      <Checkbox
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={c => onChange(c === true)}
      />
      <Label
        htmlFor={id}
        className={`cursor-pointer text-sm font-normal ${disabled ? 'text-muted-foreground' : ''}`}
      >
        {label}
      </Label>
    </div>
    <p className='ml-6 text-xs text-muted-foreground'>{hint}</p>
  </div>
);

interface CardPickerProps<T extends string> {
  label: string;
  options: ReadonlyArray<{ value: T; label: string; desc: string }>;
  value: T;
  onChange: (v: T) => void;
  columns: 2 | 3;
}

/**
 * One-of-several picker rendered as labelled cards.
 *
 * Shared so run mode and VIN processor look identical — they were the same
 * control with different padding and type scales, which made two equivalent
 * choices read as different kinds of thing.
 */
function CardPicker<T extends string>({
  label,
  options,
  value,
  onChange,
  columns
}: CardPickerProps<T>): React.ReactElement {
  return (
    <div className='space-y-1.5'>
      <Label className='text-xs text-muted-foreground'>{label}</Label>
      <div className={`grid gap-2 ${columns === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
        {options.map(option => (
          <button
            key={option.value}
            type='button'
            aria-pressed={value === option.value}
            className={`rounded-lg border px-2.5 py-2 text-left transition-colors ${
              value === option.value
                ? 'border-primary bg-primary/5'
                : 'border-border hover:bg-muted/50'
            }`}
            onClick={() => onChange(option.value)}
          >
            <p className='text-xs font-medium'>{option.label}</p>
            <p className='mt-0.5 text-[11px] leading-tight text-muted-foreground'>{option.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

interface ScheduleAdvancedSectionProps {
  runMode: 'auto' | 'full' | 'delta';
  setRunMode: (v: 'auto' | 'full' | 'delta') => void;
  advancedOpen: boolean;
  setAdvancedOpen: (open: boolean) => void;
  vinProcessorType: 'api' | 'selenium';
  setVinProcessorType: (v: 'api' | 'selenium') => void;
  skipVinProcessing: boolean;
  setSkipVinProcessing: (v: boolean) => void;
  enableDedup: boolean;
  setEnableDedup: (v: boolean) => void;
  notifyOnSuccess: boolean;
  setNotifyOnSuccess: (v: boolean) => void;
  notifyOnFailure: boolean;
  setNotifyOnFailure: (v: boolean) => void;
  notificationEmails: string[];
  setNotificationEmails: (v: string[]) => void;
  /** The list every non-overriding schedule shares, shown when not overriding. */
  sharedRecipients: string[];
  overrideRecipients: boolean;
  onOverrideRecipientsChange: (on: boolean) => void;
  minIntervalMinutes: number;
  setMinIntervalMinutes: (v: number) => void;
  description: string;
  setDescription: (v: string) => void;
}

/**
 * The settings most schedules never touch, folded away by default.
 *
 * Two of these — VIN processor and skip-VIN — were stored and round-tripped by
 * the dialog but had no control at all, so a schedule set to Selenium could
 * never be changed back through the UI.
 */
// eslint-disable-next-line max-lines-per-function
export const ScheduleAdvancedSection: React.FC<ScheduleAdvancedSectionProps> = ({
  runMode,
  setRunMode,
  advancedOpen,
  setAdvancedOpen,
  vinProcessorType,
  setVinProcessorType,
  skipVinProcessing,
  setSkipVinProcessing,
  enableDedup,
  setEnableDedup,
  notifyOnSuccess,
  setNotifyOnSuccess,
  notifyOnFailure,
  setNotifyOnFailure,
  notificationEmails,
  setNotificationEmails,
  sharedRecipients,
  overrideRecipients,
  onOverrideRecipientsChange,
  minIntervalMinutes,
  setMinIntervalMinutes,
  description,
  setDescription
}) => (
  <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
    <CollapsibleTrigger asChild>
      <Button variant='ghost' size='sm' className='gap-1 text-muted-foreground'>
        <Settings className='h-3.5 w-3.5' />
        Advanced settings
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${advancedOpen ? 'rotate-180' : ''}`}
        />
      </Button>
    </CollapsibleTrigger>

    {/* Four groups, each a heading plus its own controls, so the section reads
        as "how it runs / what it processes / who hears / limits" rather than a
        flat list of unrelated toggles. */}
    <CollapsibleContent className='space-y-4 pt-3'>
      {/* Run mode and VIN processor are the same kind of choice — pick one of
          a few named strategies — so they share a picker and sit together.
          VIN processor had no control at all before this: the value was stored
          and round-tripped, so a schedule set to Selenium could never be
          changed back through the UI. */}
      <Group title='How it runs'>
        <div className='grid gap-3 md:grid-cols-2'>
          <CardPicker
            label='Run mode'
            options={RUN_MODES}
            value={runMode}
            onChange={setRunMode}
            columns={3}
          />
          <CardPicker
            label='VIN processor'
            options={VIN_PROCESSORS}
            value={vinProcessorType}
            onChange={setVinProcessorType}
            columns={2}
          />
        </div>
      </Group>

      {/* These two are mutually exclusive — skipping VIN processing entirely
          makes "skip the ones done today" meaningless — so they belong in one
          group where that relationship is visible. */}
      <Group title='What it processes'>
        <CheckboxField
          id='schedule-skip-vin'
          checked={skipVinProcessing}
          onChange={on => {
            setSkipVinProcessing(on);
            if (on) setEnableDedup(false);
          }}
          label='Collect only — skip recall lookups'
          hint="Gathers inventory but doesn't check any VIN for recalls."
        />
        <CheckboxField
          id='schedule-enable-dedup'
          checked={enableDedup}
          disabled={skipVinProcessing}
          onChange={on => {
            setEnableDedup(on);
            if (on) setSkipVinProcessing(false);
          }}
          label='Skip VINs already processed today'
          hint={
            'When ON, the run skips VINs already processed earlier today (one recall lookup per ' +
            'VIN per day). When OFF, every collected VIN is sent to the recall processor ' +
            'regardless of prior runs.'
          }
        />
      </Group>

      <Group title='Notifications'>
        <div className='flex flex-wrap items-center gap-4'>
          <div className='flex items-center gap-1.5'>
            <Checkbox
              id='notify-success'
              checked={notifyOnSuccess}
              onCheckedChange={c => setNotifyOnSuccess(c === true)}
            />
            <Label htmlFor='notify-success' className='cursor-pointer text-sm font-normal'>
              Email on success
            </Label>
          </div>
          <div className='flex items-center gap-1.5'>
            <Checkbox
              id='notify-failure'
              checked={notifyOnFailure}
              onCheckedChange={c => setNotifyOnFailure(c === true)}
            />
            <Label htmlFor='notify-failure' className='cursor-pointer text-sm font-normal'>
              Email on failure
            </Label>
          </div>
        </div>

        {notifyOnSuccess && (
          <p className='text-xs text-muted-foreground'>
            Every recipient gets mail after each completed run, not only failures.
          </p>
        )}

        {/* Recipients are managed for every schedule at once under Settings.
            This is the escape hatch for the schedule that genuinely needs a
            different list — off by default so the shared setting stays the
            normal path. */}
        <CheckboxField
          id='schedule-override-recipients'
          checked={overrideRecipients}
          onChange={onOverrideRecipientsChange}
          label='Send to different recipients for this schedule'
          hint={
            overrideRecipients
              ? 'This schedule keeps its own list and is left alone by Settings.'
              : `Uses the shared list: ${sharedRecipients.join(', ') || 'none set'}.`
          }
        />

        {overrideRecipients && (
          <EmailChipsInput value={notificationEmails} onChange={setNotificationEmails} />
        )}
      </Group>

      <Group title='Limits and notes'>
        <div className='grid gap-3 md:grid-cols-2'>
          {/* Cooldown. Previously labelled "Minimum Interval" with help text
              describing the 409 overlap guard — a different mechanism. */}
          <div className='space-y-1.5'>
            <Label htmlFor='schedule-cooldown' className='text-xs text-muted-foreground'>
              Minimum time between runs
            </Label>
            <div className='flex items-center gap-2'>
              <Input
                id='schedule-cooldown'
                type='number'
                value={minIntervalMinutes}
                onChange={e => setMinIntervalMinutes(Number(e.target.value) || 60)}
                min={1}
                className='w-24'
              />
              <span className='text-sm text-muted-foreground'>minutes</span>
            </div>
            <p className='text-xs text-muted-foreground'>
              How long this schedule waits after a run before accepting another trigger. Sooner is
              rejected with &ldquo;try again in N seconds&rdquo;. A run still in flight is blocked
              separately, regardless of this setting.
            </p>
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='schedule-description' className='text-xs text-muted-foreground'>
              Description (optional)
            </Label>
            <Input
              id='schedule-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='Notes about this schedule'
            />
          </div>
        </div>
      </Group>
    </CollapsibleContent>
  </Collapsible>
);
